import { dataLoading } from "@/shared/decorators/LoadingAction.decorator";
import apiClient from "./ApiClientService";
import { FaultLog, SapSyncStatusResult, UpdateFaultLogLatestComments } from "@/shared/models/FaultLog";
import { IDashboardState } from "@/redux/dashboardSlice";
import { AdvancedQueryHelper } from "@/shared/utils/AdvancedQuery.helper";
import { AdvancedQueryRequest } from "@/shared/models/advanced-search/AdvancedQueryRequest";
import { CreateRangeAdvancedFilterCondition } from "@/shared/models/advanced-search/AdvancedFilterCondition";
import { Constants } from "@/shared/Constants";
import { DateTimeHelper } from "@/shared/utils/DateTime.helper";
import { Utils } from "@/shared/utils/Utils";
import { BusinessHelper } from "@/shared/utils/Business.helper";
import { CountryRegionDomain } from "@/shared/models/CountryRegionDomain";

export class FaultLogApi {
  @dataLoading([])
  static async query(
    queryParams: IDashboardState,
    CountryRegionDomains: CountryRegionDomain[],
    userDomain: string,
    userRoles: string[],
    includeTotalComment: boolean = false,
    includeAdvancedQuery: boolean = false
  ) {
    var postBody = new AdvancedQueryRequest();
    postBody.page = 1;
    postBody.pageSize = 1000;
    if (!queryParams.openAdvancedQuery) {
      postBody.filters = AdvancedQueryHelper.GenerateFilterConditions({
        Region: queryParams.filterRegionList,
        Terminal: queryParams.filterTerminalList,
        Product: queryParams.filterProductList,
        Status: queryParams.filterStatusList,
      });

      const startDateUTC = queryParams.filterStartTime && DateTimeHelper.LocalToStartUTCString(queryParams.filterStartTime);
      const endDateUTC = queryParams.filterEndTime && DateTimeHelper.LocalToEndUTCString(queryParams.filterEndTime);
      postBody.filters.push(...CreateRangeAdvancedFilterCondition(Constants.Field_ReportedTime, startDateUTC, endDateUTC));
    }

    if (queryParams.openAdvancedQuery || includeAdvancedQuery) {
      postBody.filters.push(...AdvancedQueryHelper.GenerateFromAdvancedQuery(queryParams.advancedFilters));
    }

    postBody.includeTotalComment = includeTotalComment;
    var result = await apiClient.post(`/faultlog/query-with-comments`, postBody);
    if (result.status === 200) {
      var faultLogs = result.data.data as FaultLog[];
      UpdateFaultLogLatestComments(faultLogs);

      faultLogs.forEach((row) => {
        row.domain = BusinessHelper.GetDomainByRegion(row.region!, CountryRegionDomains);
        row.needAck = BusinessHelper.IsNeedAck(row, userDomain, userRoles);
        row.isFlashing = BusinessHelper.IsFaultLogFlashing(row, userDomain, userRoles);
        row.FlashingColor = BusinessHelper.GetFlashingColor(row);
      });
      faultLogs.sort((a, b) => (b.isFlashing ? 1 : 0) - (a.isFlashing ? 1 : 0));
      return faultLogs;
    } else {
      return [];
    }
  }

  @dataLoading(false)
  static async ack(id: number) {
    var result = await apiClient.post(`/faultlog/ack`, id);
    return result.status === 200;
  }

  @dataLoading(false)
  static async SetSpontaneouslyRecoveredViewed(id: number) {
    var result = await apiClient.post(`/faultlog/set-spontaneously-recovered-viewed`, id);
    return result.status === 200;
  }

  @dataLoading(0)
  static async addFaultLog(faultLog: FaultLog) {
    faultLog = this.preProcessFaultLogForEdit(faultLog);
    var result = await apiClient.post(`/faultlog/add-with-comment`, faultLog);
    if (result.status === 200) {
      return result.data as number;
    }

    return 0;
  }

  @dataLoading(false)
  static async updateFaultLog(faultLog: FaultLog) {
    faultLog = this.preProcessFaultLogForEdit(faultLog);
    var result = await apiClient.put(`/faultlog/update-with-comment`, faultLog);
    return result.status === 200;
  }

  @dataLoading(false)
  static async updateFaultLogWithNotifySAP(faultLog: FaultLog) {
    faultLog = this.preProcessFaultLogForEdit(faultLog);
    var result = await apiClient.put(`/faultlog/update-with-sap-notify`, faultLog);
    return result.status === 200;
  }

  @dataLoading({})
  static async getDevicePicFiles(fileNames: string[]): Promise<Record<string, string>> {
    var result = await apiClient.post(`/faultlog/get-device-pic-files`, fileNames);
    if (result.status === 200) {
      return result.data as Record<string, string>;
    }
    return {};
  }

  @dataLoading(false)
  static async syncSapStatus(workOrderNumber: string, sapNotificationNumber?: number): Promise<SapSyncStatusResult | null> {
    const body = {
      workOrderNumber: workOrderNumber || undefined,
      sapNotificationNumber: sapNotificationNumber ? sapNotificationNumber.toString() : undefined,
    };
    var result = await apiClient.post(`/faultlog/sync-sap-status`, body);
    if (result.status === 200) {
      return result.data as SapSyncStatusResult;
    }
    return null;
  }

  private static preProcessFaultLogForEdit(faultLog: FaultLog): FaultLog {
    if (!faultLog) {
      return faultLog;
    }

    if (Utils.isEmpty(faultLog.numOfDeliveries)) {
      faultLog.numOfDeliveries = undefined;
    }

    if (Utils.isEmpty(faultLog.sapNotificationNumber)) {
      faultLog.sapNotificationNumber = undefined;
    }

    return faultLog;
  }
}
